import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ChevronRight, Navigation, LocateFixed, MapPinOff, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, fmtDate } from "@/lib/api";
import { DEFAULT_CENTER, hasCoords } from "@/lib/map";
import { directionsUrl, legKm, localDay, optimizeRoute } from "@/lib/route";
import { useLocationReporter } from "@/hooks/useLocationReporter";
import { NotificationBell } from "@/components/NotificationBell";

const numIcon = (n) =>
  L.divIcon({
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    html: `<div style="width:30px;height:30px;border-radius:50%;background:#D7262E;color:#fff;display:grid;place-items:center;font:800 14px Cairo,sans-serif;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.4)">${n}</div>`,
  });
const meIcon = L.divIcon({ className: "", iconSize: [18, 18], iconAnchor: [9, 9], html: `<div style="width:18px;height:18px;border-radius:50%;background:#3B82F6;border:3px solid #fff;box-shadow:0 0 0 6px rgba(59,130,246,.3)"></div>` });

const Fit = ({ pts }) => {
  const map = useMap();
  const key = pts.map((p) => p.join(",")).join("|");
  useEffect(() => {
    if (pts.length === 1) map.setView(pts[0], 14);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [30, 30] });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

export default function DailyRoute() {
  useLocationReporter();
  const [items, setItems] = useState([]);
  const [params] = useSearchParams();
  const [day, setDay] = useState(params.get("day") || localDay());
  useEffect(() => {
    if (params.get("day")) setDay(params.get("day"));
  }, [params]);
  const [me, setMe] = useState(null);
  const [locError, setLocError] = useState(false);
  const [mode, setMode] = useState("distance");

  useEffect(() => {
    api.get("/inspections").then((r) => setItems(r.data)).catch(() => {});
    navigator.geolocation?.getCurrentPosition(
      (p) => setMe({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setLocError(true),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, []);

  const todays = items.filter((i) => (i.scheduled_at || "").startsWith(day) && ["assigned", "in_progress"].includes(i.status));
  const placed = todays.filter((i) => hasCoords(i.customer));
  const unplaced = todays.filter((i) => !hasCoords(i.customer));
  const route = useMemo(() => {
    const stops = placed.map((i) => ({ lat: i.customer.lat, lng: i.customer.lng, insp: i }));
    if (mode === "time") return stops.sort((a, b) => a.insp.scheduled_at.localeCompare(b.insp.scheduled_at));
    return optimizeRoute(me, stops);
  }, [placed.map((i) => i.id).join(), me, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const timeConflict = mode === "distance" && route.some((p, i) => i > 0 && p.insp.scheduled_at < route[i - 1].insp.scheduled_at);
  const legs = legKm(me, route);
  const total = legs.reduce((a, b) => a + b, 0);
  const line = [...(me ? [[me.lat, me.lng]] : []), ...route.map((p) => [p.lat, p.lng])];

  return (
    <div className="dark min-h-screen bg-ink text-slate-100">
      <header className="sticky top-0 z-[1000] bg-ink/90 backdrop-blur-md border-b border-line">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center gap-3">
          <Link to="/agent" aria-label="رجوع" data-testid="route-back-link" className="h-11 w-11 grid place-items-center rounded-lg border border-line hover:bg-panel-hover"><ChevronRight size={20} /></Link>
          <p className="font-heading font-bold text-lg me-auto whitespace-nowrap">مسار الزيارات</p>
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-36 sm:w-40 h-11 bg-panel border-line" data-testid="route-day-input" />
          <NotificationBell dark loud />
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-panel border border-line p-3"><p className="text-xs text-slate-400">زيارات</p><p className="font-mono text-2xl font-bold" data-testid="route-stops-count">{todays.length}</p></div>
          <div className="rounded-xl bg-panel border border-line p-3"><p className="text-xs text-slate-400">على الخريطة</p><p className="font-mono text-2xl font-bold">{route.length}</p></div>
          <div className="rounded-xl bg-panel border border-line p-3"><p className="text-xs text-slate-400">المسافة التقريبية</p><p className="font-mono text-2xl font-bold" data-testid="route-total-km">{total.toFixed(1)}<span className="text-xs ms-1">كم</span></p></div>
        </div>
        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-panel border border-line">
          {[["distance", "أقصر مسافة"], ["time", "حسب الموعد"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`route-mode-${k}`}
              className={`h-10 rounded-lg text-sm font-bold transition-colors ${mode === k ? "bg-brand-red text-white" : "text-slate-300 hover:bg-panel-hover"}`}>{l}</button>
          ))}
        </div>
        {timeConflict && <p className="text-xs text-amber-300" data-testid="route-time-conflict">تنبيه: ترتيب أقصر مسافة لا يطابق ترتيب المواعيد. راجع الأوقات أو اختر «حسب الموعد».</p>}
        {locError && <p className="text-xs text-amber-300 flex items-center gap-1.5" data-testid="route-location-error"><LocateFixed size={14} />لم يُسمح بالموقع؛ يبدأ المسار من أول زيارة.</p>}
        <div dir="ltr" className="relative z-0 h-[45vh] rounded-xl overflow-hidden border border-line" data-testid="route-map">
          <MapContainer center={DEFAULT_CENTER} zoom={5} className="h-full w-full">
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Fit pts={line} />
            {line.length > 1 && <Polyline positions={line} pathOptions={{ color: "#D7262E", weight: 4, dashArray: "8 8" }} />}
            {me && <Marker position={[me.lat, me.lng]} icon={meIcon}><Popup>موقعي</Popup></Marker>}
            {route.map((p, i) => (
              <Marker key={p.insp.id} position={[p.lat, p.lng]} icon={numIcon(i + 1)}>
                <Popup><div dir="rtl" className="text-right font-[Cairo]"><b>{p.insp.customer.name}</b><br />{p.insp.ref}</div></Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        {route.length > 0 && (
          <a href={directionsUrl(me, route)} target="_blank" rel="noreferrer" data-testid="route-start-navigation"
            className="h-14 rounded-xl bg-brand-red text-white font-bold flex items-center justify-center gap-2 hover:bg-brand-red-dark">
            <Navigation size={20} />ابدأ الملاحة بالترتيب في Google Maps
          </a>
        )}
        {route.length > 10 && <p className="text-xs text-slate-400">Google Maps يدعم أول 10 محطات فقط في رابط واحد.</p>}
        {todays.length === 0 && <p className="text-center text-slate-400 py-10" data-testid="route-empty">لا توجد زيارات في هذا اليوم</p>}
        <ol className="space-y-2" data-testid="route-list">
          {route.map((p, i) => (
            <li key={p.insp.id}>
              <Link to={`/agent/inspections/${p.insp.id}`} data-testid={`route-stop-${i + 1}`} className="flex items-center gap-3 rounded-xl bg-panel border border-line p-3 hover:border-brand-sky/70">
                <span className="h-9 w-9 shrink-0 rounded-full bg-brand-red grid place-items-center font-bold">{i + 1}</span>
                <div className="min-w-0 me-auto">
                  <p className="font-bold truncate">{p.insp.customer.name}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1"><Clock size={12} />{fmtDate(p.insp.scheduled_at).split(" - ")[1]} · {p.insp.customer.area || p.insp.ref}</p>
                </div>
                <span className="font-mono text-sm text-brand-sky">{legs[i] ? `${legs[i].toFixed(1)} كم` : "البداية"}</span>
              </Link>
            </li>
          ))}
        </ol>
        {unplaced.length > 0 && (
          <div className="rounded-xl border border-line p-3" data-testid="route-unplaced">
            <p className="text-sm font-bold flex items-center gap-1.5 mb-2"><MapPinOff size={15} />بدون موقع محدد ({unplaced.length})</p>
            {unplaced.map((i) => <Link key={i.id} to={`/agent/inspections/${i.id}`} className="block text-sm text-slate-300 py-1">{i.customer.name} · {i.customer.area || i.ref}</Link>)}
          </div>
        )}
        <p className="text-[11px] text-slate-500">«أقصر مسافة» يحسب الترتيب بالمسافة المباشرة بين المواقع، والملاحة الفعلية على الطرق تتم عبر Google Maps.</p>
      </main>
    </div>
  );
}
